import { FieldValue, FieldValues, UseFormReturn } from "react-hook-form";
import { SiteFormValues } from "../siteSchema";
import { ExternalServiceOption, useFetchExternalServiceOptions, useReindexExternalConnection } from "@/hooks/externalConnectionsApi";
import { ProjectFullDto } from "../siteApi";
import ExternalFilter from "@/features/ExternalConnections/Filters/SingleFilterInput";
import ExternalConnectionFilters from "@/features/ExternalConnections/Filters/ExternalConnectionFilters";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loading";
import { Spinner } from "@/components/ui/spinner";
import { RecordValueType } from "@/lib/utils";

export interface ExtServiceConfigProps {
    form?: UseFormReturn<SiteFormValues>;
    siteData: ProjectFullDto | null | undefined;
}

export interface ExtServiceCardProps extends ExtServiceConfigProps {
    option: ExternalServiceOption;
}

function ExtServiceCard({ option: starterOption, siteData, form }: ExtServiceCardProps) {
    const serviceOptions = siteData?.externalServiceOptions ?? {};
    const disabled = !form;

    const [option, setServiceOption] = useState(starterOption);

    const values = (option.id in serviceOptions && serviceOptions[option.id]) || {};

    const {
        toast
    } = useToast();
    const [siteOptionValue, setSiteOptionValue] = useState(values);


    const queryClient = useQueryClient();

    const {
        mutateAsync: reindexExternalConnection,
        isPending: reindexPending,
    } = useReindexExternalConnection({ queryClient, id: option.id })

    useEffect(() => {
        const curr: ProjectFullDto["externalServiceOptions"] = form?.getValues("externalServiceOptions") ?? {};
        curr[option.id] = siteOptionValue;


        form?.setValue('externalServiceOptions', curr)
    }, [siteOptionValue, form, option.id]);

    if (!option?.deviceCreationKeyValues || option?.deviceCreationKeyValues?.length === 0) {
        return <></>
    }

    return <section className="mt-6 rounded-xl border bg-[#111] p-6">
        <div >
            <div className="flex flex-row justify-between">
                <h2 className="text-xl font-semibold">
                    {option.name}
                    {reindexPending && <Spinner className="" size={"small"} outerStyles="inline-block align-top px-5 " />}
                </h2>
                <Button
                    variant={"ghost"}
                    type={"button"}
                    disabled={reindexPending}
                    onClick={() => reindexExternalConnection(null).then((data) => {
                        console.log(option, data)
                        setServiceOption({ ...option, ...data });
                        toast({ title: `Successfully reindexed ${option.name}`, duration: 5000 });
                    }).catch((error) => {
                        toast({ title: `Failed to Reindex ${option.name}`, description: error.message, variant: 'destructive' });
                    })}
                >
                    ReIndex Service
                </Button>
            </div>
            {option.description && <p className="text-gray-400">{option.description}</p>}
        </div>
        <div className="">
            <ExternalConnectionFilters
                externalConnection={option}
                processing={false}
                values={values}
                onChange={setSiteOptionValue}
                disabled={disabled || reindexPending}
            />
        </div>
    </section>
}


export default function ExtServiceConfig({ form, siteData }: ExtServiceConfigProps) {

    const {
        data: externalServiceOptions,
        error,
    } = useFetchExternalServiceOptions();

    if (error) {
        console.error(error);

        return <>
            <section className="mt-6 rounded-xl border bg-[#111]">
                <div className="flex justify-between items-center p-6">
                    <h2 className="text-xl font-semibold">External Service Configuration</h2>
                </div>
                <div className="p-6">
                    <p className="text-gray-400">Error loading external service options</p>
                </div>
            </section>
        </>
    }

    return <> {(externalServiceOptions && externalServiceOptions.externalOptions?.map((option, i) => (
        <ExtServiceCard key={i} option={option} form={form} siteData={siteData} />
    )))}
    </>

}